import { TotalSupplyAbi, WrenNavReadAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Wren's self-priced NAV token (`wstGBP` over `tGBP`) — four calls:
 *   1. `totalSupply()`
 *   2. `burncost()`  — what a redemption actually pays, 1e18
 *   3. `navprice()`  — the restated NAV, 1e18, monotonic
 *   4. `burnable()`  — is the exit switched on at all
 *
 * Not ERC-4626 in any part: `asset()`, `totalAssets()`, `convertToAssets()`
 * and every `max*` view revert. Unlike [navOracle](./navOracle.ts), where the
 * price lives in a separate Chainlink-shaped feed the token knows nothing
 * about, here the token prices ITSELF — `navprice()` is a stored figure the
 * issuer restates on its own schedule (measured: weekly steps of ~6.7 bps).
 * There is no second contract to point a `priceOracle` at, hence a reader.
 *
 * **Two prices, and the redeemable one wins.** `burncost()` sits BELOW
 * `navprice()` — 1.006198 against 1.008720 on 2026-08-14, a ~25 bps haircut on
 * the way out that is a real cost, not rounding. Following the Yield Basis
 * precedent, `exchangeRate` is the REDEEMABLE price (what a holder can
 * actually take out) and `fundamentalExchangeRate` is the NAV, so
 * `fetchPublic` derives the signed `redemptionDiscountBps` between them and a
 * consumer can see the gap instead of inheriting the optimistic number. The
 * APR is measured off `navprice()` in `fetchers/wren.ts`, which is the leg
 * that actually accrues.
 *
 * `burnable()` is a 0/1 switch on the redemption path, surfaced as
 * `instantRedeemEnabled`. An unreadable answer is treated as ENABLED: the
 * registry already declares this row `instant`, and zeroing the flag on an RPC
 * hiccup would read as a frozen vault.
 */
export const readerWrenNav = (entry: SavingsRegistryEntry): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'burncost', params: [] },
      { address: entry.address, name: 'navprice', params: [] },
      { address: entry.address, name: 'burnable', params: [] },
    ],
    abis: [TotalSupplyAbi, WrenNavReadAbi, WrenNavReadAbi, WrenNavReadAbi],
    parse: ([supply, burn, nav, burnable]) => {
      const totalSupply = toBigInt(supply)
      const navPrice = toBigInt(nav)
      if (totalSupply === undefined) return undefined
      // A non-positive NAV is a broken restatement, not a zero-value vault —
      // drop the entry rather than publish a 0 price.
      if (navPrice === undefined || navPrice <= 0n) return undefined

      // Redeemable price, falling back to the NAV when the getter fails so a
      // missed read understates nothing; the discount then reads as absent
      // rather than as zero.
      const burncost = toBigInt(burn)
      const hasBurncost = burncost !== undefined && burncost > 0n
      const exchangeRate = hasBurncost ? burncost : navPrice

      const totalAssets =
        (totalSupply * exchangeRate * underlyingUnit) / (ONE_E18 * shareUnit)

      const burnFlag = toBigInt(burnable)
      const instantRedeemEnabled = burnFlag === undefined ? true : burnFlag > 0n

      return {
        totalAssets,
        totalSupply,
        exchangeRate,
        fundamentalExchangeRate: hasBurncost ? navPrice : undefined,
        instantRedeemEnabled,
      }
    },
  }
}
